import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChannelTestResult,
  changePassword,
  FirmNotificationSettings,
  FirmProfile,
  getFirmProfile,
  getFirmSettings,
  getProfile,
  getStorageSettings,
  getStreamSettings,
  parseTokenClaims,
  ProfileRecord,
  saveFirmProfile,
  saveFirmSettings,
  saveStorageSettings,
  saveStreamSettings,
  setToken,
  StorageSettings,
  StreamSettings,
  testFirmChannel,
  testStorageSettings,
  updateProfile,
} from "../api";

type Tab =
  | "company"
  | "smtp"
  | "sms"
  | "whatsapp"
  | "streaming"
  | "storage"
  | "profile"
  | "password";

// Scope: "firm" = per-firm config (firm ADMIN owns it); "platform" = system-wide
// config (SUPERADMIN only); "all" = self-service for any authenticated user.
type TabScope = "firm" | "platform" | "all";

const TABS: { id: Tab; labelKey: string; scope: TabScope }[] = [
  { id: "company", labelKey: "settings.tabs.company", scope: "firm" },
  { id: "smtp", labelKey: "settings.tabs.smtp", scope: "firm" },
  { id: "sms", labelKey: "settings.tabs.sms", scope: "firm" },
  { id: "whatsapp", labelKey: "settings.tabs.whatsapp", scope: "firm" },
  { id: "streaming", labelKey: "settings.tabs.streaming", scope: "platform" },
  { id: "storage", labelKey: "settings.tabs.storage", scope: "platform" },
  { id: "profile", labelKey: "settings.tabs.profile", scope: "all" },
  { id: "password", labelKey: "settings.tabs.password", scope: "all" },
];

export function SettingsPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const isAdmin = claims?.role === "ADMIN";
  const isSuper = claims?.role === "SUPERADMIN";
  const myFirm = claims?.firm_id ?? "";

  const canSee = (scope: TabScope) =>
    scope === "all" ||
    (scope === "firm" && isAdmin) ||
    (scope === "platform" && isSuper);

  const availableTabs = TABS.filter((item) => canSee(item.scope));
  const [tab, setTab] = useState<Tab>(
    isSuper ? "streaming" : isAdmin ? "company" : "profile",
  );

  return (
    <div className="page">
      <h2>{t("settings.title")}</h2>
      <p className="muted">
        {t("settings.firmRole", { firm: myFirm, role: claims?.role ?? "-" })}
      </p>

      <div className="tabs">
        {availableTabs.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`tab ${tab === item.id ? "active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {tab === "company" && isAdmin && <CompanyTab />}
        {tab === "smtp" && isAdmin && <SmtpTab firmId={myFirm} />}
        {tab === "sms" && isAdmin && <SmsTab firmId={myFirm} />}
        {tab === "whatsapp" && isAdmin && <WhatsAppTab firmId={myFirm} />}
        {tab === "streaming" && isSuper && <StreamingTab />}
        {tab === "storage" && isSuper && <StorageTab />}
        {tab === "profile" && <ProfileTab />}
        {tab === "password" && <PasswordTab />}
      </div>
    </div>
  );
}

function useFirmSettings(firmId: string) {
  const { t } = useTranslation("dashboard");
  const [settings, setSettings] = useState<FirmNotificationSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFirmSettings(firmId)
      .then(setSettings)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [firmId]);

  async function save(patch: Partial<FirmNotificationSettings>) {
    setError(null);
    setInfo(null);
    try {
      const merged: FirmNotificationSettings = {
        ...(settings ?? { firm_id: firmId }),
        ...patch,
        firm_id: firmId,
      };
      const saved = await saveFirmSettings(firmId, merged);
      setSettings(saved);
      setInfo(t("settings.saved"));
    } catch (e) {
      setError(String(e));
    }
  }

  return { settings, save, error, info, loading };
}

export function SmtpTab({ firmId }: { firmId: string }) {
  const { t } = useTranslation(["dashboard", "common"]);
  const { settings, save, error, info, loading } = useFirmSettings(firmId);
  const [form, setForm] = useState<FirmNotificationSettings | null>(null);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  if (loading || !form) return <p>{t("common:actions.loading")}</p>;

  function update<K extends keyof FirmNotificationSettings>(
    key: K,
    value: FirmNotificationSettings[K],
  ) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  return (
    <form
      className="settings-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (form) save(form);
      }}
    >
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <p className="muted">{t("settings.smtp.intro")}</p>
      <label>
        {t("settings.smtp.host")}
        <input value={form.smtp_host ?? ""} onChange={(e) => update("smtp_host", e.target.value)}
               placeholder="smtp.gmail.com" />
      </label>
      <label>
        {t("settings.smtp.port")}
        <input type="number" value={form.smtp_port ?? 587}
               onChange={(e) => update("smtp_port", Number(e.target.value))} />
      </label>
      <label>
        {t("settings.smtp.sender")}
        <input value={form.smtp_sender ?? ""}
               onChange={(e) => update("smtp_sender", e.target.value)}
               placeholder="noreply@firma.com" />
      </label>
      <label>
        {t("settings.smtp.username")}
        <input value={form.smtp_username ?? ""}
               onChange={(e) => update("smtp_username", e.target.value)} />
      </label>
      <label>
        {t("settings.smtp.password")}
        <input type="password" value={form.smtp_password ?? ""}
               onChange={(e) => update("smtp_password", e.target.value)}
               placeholder={t("settings.smtp.passwordPlaceholder")} />
      </label>
      <label className="checkbox">
        <input type="checkbox" checked={!!form.smtp_use_tls}
               onChange={(e) => update("smtp_use_tls", e.target.checked)} />
        {t("settings.smtp.useTls")}
      </label>
      <button type="submit">{t("common:actions.save")}</button>
      <ChannelTestBlock firmId={firmId} channel="email" hint={t("settings.test.hintEmail")} />
    </form>
  );
}

function ChannelTestBlock({
  firmId,
  channel,
  hint,
}: {
  firmId: string;
  channel: "email" | "sms" | "whatsapp";
  hint: string;
}) {
  const { t } = useTranslation("dashboard");
  const [destination, setDestination] = useState("");
  const [result, setResult] = useState<ChannelTestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const placeholder =
    channel === "email" ? "ornek@firma.com" : "+90 5XX XXX XX XX";

  async function onTest(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (!destination.trim()) {
      setError(t("settings.test.needDestination"));
      return;
    }
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await testFirmChannel(firmId, channel, destination.trim());
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="test-block">
      <div className="test-block-head">
        <strong>{t("settings.test.title")}</strong>
        <span className="muted small">{hint}</span>
      </div>
      <div className="test-block-row">
        <input
          type="text"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          placeholder={placeholder}
        />
        <button type="button" className="ghost" onClick={onTest} disabled={busy}>
          {busy ? t("settings.test.sending") : t("settings.test.send")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {result && (
        <p className={result.status === "sent" ? "info" : "error"}>
          {result.status === "sent" ? "✓ " : "✗ "}
          {result.adapter}: {result.status}
          {result.detail ? ` — ${result.detail}` : ""}
        </p>
      )}
    </div>
  );
}

export function SmsTab({ firmId }: { firmId: string }) {
  const { t } = useTranslation(["dashboard", "common"]);
  const { settings, save, error, info, loading } = useFirmSettings(firmId);
  const [form, setForm] = useState<FirmNotificationSettings | null>(null);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  if (loading || !form) return <p>{t("common:actions.loading")}</p>;

  function update<K extends keyof FirmNotificationSettings>(
    key: K, value: FirmNotificationSettings[K],
  ) { setForm((f) => (f ? { ...f, [key]: value } : f)); }

  return (
    <form className="settings-form" onSubmit={(e) => { e.preventDefault(); if (form) save(form); }}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <p className="muted">{t("settings.sms.intro")}</p>
      <label>{t("settings.sms.usercode")}
        <input value={form.netgsm_usercode ?? ""}
               onChange={(e) => update("netgsm_usercode", e.target.value)} />
      </label>
      <label>{t("settings.sms.password")}
        <input type="password" value={form.netgsm_password ?? ""}
               onChange={(e) => update("netgsm_password", e.target.value)} />
      </label>
      <label>{t("settings.sms.header")}
        <input value={form.netgsm_header ?? ""}
               onChange={(e) => update("netgsm_header", e.target.value)}
               placeholder={t("settings.sms.headerPlaceholder")} />
      </label>
      <button type="submit">{t("common:actions.save")}</button>
      <ChannelTestBlock firmId={firmId} channel="sms" hint={t("settings.test.hintSms")} />
    </form>
  );
}

export function WhatsAppTab({ firmId }: { firmId: string }) {
  const { t } = useTranslation(["dashboard", "common"]);
  const { settings, save, error, info, loading } = useFirmSettings(firmId);
  const [form, setForm] = useState<FirmNotificationSettings | null>(null);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  if (loading || !form) return <p>{t("common:actions.loading")}</p>;

  function update<K extends keyof FirmNotificationSettings>(
    key: K, value: FirmNotificationSettings[K],
  ) { setForm((f) => (f ? { ...f, [key]: value } : f)); }

  const provider = form.whatsapp_provider ?? "log";

  return (
    <form className="settings-form" onSubmit={(e) => { e.preventDefault(); if (form) save(form); }}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <label>{t("settings.whatsapp.provider")}
        <select value={provider}
                onChange={(e) => update("whatsapp_provider", e.target.value as "log" | "netgsm" | "meta")}>
          <option value="log">{t("settings.whatsapp.providerLog")}</option>
          <option value="netgsm">{t("settings.whatsapp.providerNetgsm")}</option>
          <option value="meta">{t("settings.whatsapp.providerMeta")}</option>
        </select>
      </label>
      {provider === "netgsm" && (
        <>
          <label>{t("settings.whatsapp.netgsmUsercode")}
            <input value={form.whatsapp_netgsm_usercode ?? ""}
                   onChange={(e) => update("whatsapp_netgsm_usercode", e.target.value)} />
          </label>
          <label>{t("settings.whatsapp.netgsmPassword")}
            <input type="password" value={form.whatsapp_netgsm_password ?? ""}
                   onChange={(e) => update("whatsapp_netgsm_password", e.target.value)} />
          </label>
        </>
      )}
      {provider === "meta" && (
        <>
          <label>{t("settings.whatsapp.metaToken")}
            <input type="password" value={form.whatsapp_meta_token ?? ""}
                   onChange={(e) => update("whatsapp_meta_token", e.target.value)} />
          </label>
          <label>{t("settings.whatsapp.metaPhoneId")}
            <input value={form.whatsapp_meta_phone_number_id ?? ""}
                   onChange={(e) => update("whatsapp_meta_phone_number_id", e.target.value)} />
          </label>
        </>
      )}
      <button type="submit">{t("common:actions.save")}</button>
      <ChannelTestBlock firmId={firmId} channel="whatsapp" hint={t("settings.test.hintWhatsapp")} />
    </form>
  );
}

function CompanyTab() {
  const { t } = useTranslation(["dashboard", "common"]);
  const [form, setForm] = useState<FirmProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFirmProfile()
      .then(setForm)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>{t("common:actions.loading")}</p>;
  if (!form) return <p className="error">{error ?? t("settings.company.loadError")}</p>;

  function update<K extends keyof FirmProfile>(key: K, value: FirmProfile[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!form) return;
    try {
      const saved = await saveFirmProfile({
        name: form.name,
        legal_name: form.legal_name ?? null,
        tax_number: form.tax_number ?? null,
        tax_office: form.tax_office ?? null,
        address: form.address ?? null,
        city: form.city ?? null,
        phone: form.phone ?? null,
        email: form.email ?? null,
        kvkk_contact: form.kvkk_contact ?? null,
        logo_url: form.logo_url ?? null,
      });
      setForm(saved);
      setInfo(t("settings.saved"));
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <form className="settings-form" onSubmit={onSave}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <p className="muted">{t("settings.company.intro", { id: form.id })}</p>
      <label>
        {t("settings.company.name")}
        <input value={form.name ?? ""} onChange={(e) => update("name", e.target.value)} required />
      </label>
      <label>
        {t("settings.company.legalName")}
        <input
          value={form.legal_name ?? ""}
          onChange={(e) => update("legal_name", e.target.value)}
        />
      </label>
      <label>
        {t("settings.company.taxNumber")}
        <input
          value={form.tax_number ?? ""}
          onChange={(e) => update("tax_number", e.target.value)}
        />
      </label>
      <label>
        {t("settings.company.taxOffice")}
        <input
          value={form.tax_office ?? ""}
          onChange={(e) => update("tax_office", e.target.value)}
        />
      </label>
      <label>
        {t("settings.company.address")}
        <input value={form.address ?? ""} onChange={(e) => update("address", e.target.value)} />
      </label>
      <label>
        {t("settings.company.city")}
        <input value={form.city ?? ""} onChange={(e) => update("city", e.target.value)} />
      </label>
      <label>
        {t("settings.company.phone")}
        <input value={form.phone ?? ""} onChange={(e) => update("phone", e.target.value)} />
      </label>
      <label>
        {t("settings.company.email")}
        <input
          type="email"
          value={form.email ?? ""}
          onChange={(e) => update("email", e.target.value)}
        />
      </label>
      <label>
        {t("settings.company.kvkkContact")}
        <input
          value={form.kvkk_contact ?? ""}
          onChange={(e) => update("kvkk_contact", e.target.value)}
          placeholder={t("settings.company.kvkkPlaceholder")}
        />
      </label>
      <label>
        {t("settings.company.logoUrl")}
        <input
          value={form.logo_url ?? ""}
          onChange={(e) => update("logo_url", e.target.value)}
          placeholder="https://..."
        />
      </label>
      <button type="submit">{t("common:actions.save")}</button>
    </form>
  );
}

function StreamingTab() {
  const { t } = useTranslation(["dashboard", "common"]);
  const [form, setForm] = useState<StreamSettings | null>(null);
  const [meta, setMeta] = useState<StreamSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStreamSettings()
      .then((s) => {
        setForm(s);
        setMeta(s);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !form) return <p>{t("common:actions.loading")}</p>;

  function update<K extends keyof StreamSettings>(key: K, value: StreamSettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!form) return;
    try {
      const saved = await saveStreamSettings({
        livekit_url: form.livekit_url ?? null,
        livekit_api_key: form.livekit_api_key ?? null,
        livekit_api_secret: form.livekit_api_secret ?? null,
        token_ttl_seconds: form.token_ttl_seconds ?? null,
      });
      setForm(saved);
      setMeta(saved);
      setInfo(t("settings.saved"));
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <form className="settings-form" onSubmit={onSave}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <p className="muted">{t("settings.streaming.intro")}</p>
      <p className={meta?.mode === "live" ? "info" : "muted"}>
        {t("settings.streaming.mode")}:{" "}
        <strong>
          {meta?.mode === "live"
            ? t("settings.streaming.modeLive")
            : t("settings.streaming.modeMock")}
        </strong>
        {meta?.source ? ` · ${t("settings.streaming.source")}: ${meta.source}` : ""}
      </p>
      <label>
        {t("settings.streaming.url")}
        <input
          value={form.livekit_url ?? ""}
          onChange={(e) => update("livekit_url", e.target.value)}
          placeholder="wss://livekit.aicruiseeye.com"
        />
      </label>
      <label>
        {t("settings.streaming.apiKey")}
        <input
          value={form.livekit_api_key ?? ""}
          onChange={(e) => update("livekit_api_key", e.target.value)}
        />
      </label>
      <label>
        {t("settings.streaming.apiSecret")}
        <input
          type="password"
          value={form.livekit_api_secret ?? ""}
          onChange={(e) => update("livekit_api_secret", e.target.value)}
          placeholder={t("settings.smtp.passwordPlaceholder")}
        />
      </label>
      <label>
        {t("settings.streaming.ttl")}
        <input
          type="number"
          value={form.token_ttl_seconds ?? 3600}
          onChange={(e) => update("token_ttl_seconds", Number(e.target.value))}
        />
      </label>
      <button type="submit">{t("common:actions.save")}</button>
    </form>
  );
}

function StorageTab() {
  const { t } = useTranslation(["dashboard", "common"]);
  const [form, setForm] = useState<StorageSettings | null>(null);
  const [meta, setMeta] = useState<StorageSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ status: string; detail: string } | null>(
    null,
  );

  useEffect(() => {
    getStorageSettings()
      .then((s) => {
        setForm(s);
        setMeta(s);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !form) return <p>{t("common:actions.loading")}</p>;

  function update<K extends keyof StorageSettings>(key: K, value: StorageSettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!form) return;
    try {
      const saved = await saveStorageSettings({
        minio_endpoint: form.minio_endpoint ?? null,
        minio_access_key: form.minio_access_key ?? null,
        minio_secret_key: form.minio_secret_key ?? null,
        minio_bucket: form.minio_bucket ?? null,
        minio_secure: form.minio_secure ?? false,
        receipt_url_ttl_seconds: form.receipt_url_ttl_seconds ?? null,
      });
      setForm(saved);
      setMeta(saved);
      setInfo(t("settings.saved"));
    } catch (e) {
      setError(String(e));
    }
  }

  async function onTest() {
    setTestResult(null);
    setTestBusy(true);
    try {
      setTestResult(await testStorageSettings());
    } catch (e) {
      setTestResult({ status: "failed", detail: String(e) });
    } finally {
      setTestBusy(false);
    }
  }

  return (
    <form className="settings-form" onSubmit={onSave}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <p className="muted">{t("settings.storage.intro")}</p>
      <p className={meta?.configured ? "info" : "muted"}>
        {t("settings.storage.configured")}:{" "}
        <strong>
          {meta?.configured ? t("settings.storage.yes") : t("settings.storage.no")}
        </strong>
        {meta?.source ? ` · ${t("settings.streaming.source")}: ${meta.source}` : ""}
      </p>
      <label>
        {t("settings.storage.endpoint")}
        <input
          value={form.minio_endpoint ?? ""}
          onChange={(e) => update("minio_endpoint", e.target.value)}
          placeholder="minio.aicruiseeye.com:9000"
        />
      </label>
      <label>
        {t("settings.storage.accessKey")}
        <input
          value={form.minio_access_key ?? ""}
          onChange={(e) => update("minio_access_key", e.target.value)}
        />
      </label>
      <label>
        {t("settings.storage.secretKey")}
        <input
          type="password"
          value={form.minio_secret_key ?? ""}
          onChange={(e) => update("minio_secret_key", e.target.value)}
          placeholder={t("settings.smtp.passwordPlaceholder")}
        />
      </label>
      <label>
        {t("settings.storage.bucket")}
        <input
          value={form.minio_bucket ?? ""}
          onChange={(e) => update("minio_bucket", e.target.value)}
          placeholder="aice-receipts"
        />
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={!!form.minio_secure}
          onChange={(e) => update("minio_secure", e.target.checked)}
        />
        {t("settings.storage.secure")}
      </label>
      <label>
        {t("settings.storage.ttl")}
        <input
          type="number"
          value={form.receipt_url_ttl_seconds ?? 600}
          onChange={(e) => update("receipt_url_ttl_seconds", Number(e.target.value))}
        />
      </label>
      <button type="submit">{t("common:actions.save")}</button>
      <div className="test-block">
        <div className="test-block-row">
          <button type="button" className="ghost" onClick={onTest} disabled={testBusy}>
            {testBusy ? t("settings.storage.testing") : t("settings.storage.test")}
          </button>
        </div>
        {testResult && (
          <p className={testResult.status === "ok" ? "info" : "error"}>
            {testResult.status === "ok" ? "✓ " : "✗ "}
            {testResult.detail}
          </p>
        )}
      </div>
    </form>
  );
}

function ProfileTab() {
  const { t } = useTranslation(["dashboard", "common"]);
  const [profile, setProfile] = useState<ProfileRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>{t("common:actions.loading")}</p>;
  if (!profile) return <p className="error">{error ?? t("settings.profile.loadError")}</p>;

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!profile) return;
    try {
      const updated = await updateProfile({
        display_name: profile.display_name,
        email: profile.email,
        phone: profile.phone,
      });
      setProfile(updated);
      setInfo(t("settings.profile.updated"));
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <form className="settings-form" onSubmit={onSave}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <label>{t("settings.profile.username")}
        <input value={profile.username} disabled />
      </label>
      <label>{t("settings.profile.displayName")}
        <input value={profile.display_name ?? ""}
               onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} />
      </label>
      <label>{t("settings.profile.email")}
        <input type="email" value={profile.email ?? ""}
               onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
      </label>
      <label>{t("settings.profile.phone")}
        <input value={profile.phone ?? ""}
               onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
      </label>
      <button type="submit">{t("common:actions.save")}</button>
    </form>
  );
}

function PasswordTab() {
  const { t } = useTranslation(["dashboard", "common"]);
  const navigate = useNavigate();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPwd.length < 8) { setError(t("changePassword.minLength")); return; }
    if (newPwd !== confirm) { setError(t("changePassword.mismatch")); return; }
    setLoading(true);
    try {
      await changePassword(oldPwd, newPwd);
      setInfo(t("settings.password.changed"));
      setToken(null);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="settings-form" onSubmit={onSubmit}>
      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}
      <label>{t("settings.password.current")}
        <input type="password" value={oldPwd}
               onChange={(e) => setOldPwd(e.target.value)} required />
      </label>
      <label>{t("settings.password.new")}
        <input type="password" value={newPwd}
               onChange={(e) => setNewPwd(e.target.value)} required />
      </label>
      <label>{t("settings.password.confirm")}
        <input type="password" value={confirm}
               onChange={(e) => setConfirm(e.target.value)} required />
      </label>
      <button type="submit" disabled={loading}>
        {loading ? t("common:actions.saving") : t("settings.password.changeButton")}
      </button>
    </form>
  );
}
